import * as React from 'react'
import { SidebarCloseIcon, ListViewIcon } from '../components/Icons'
import { useThumbnailLoaderWithRetry, isDirectoryItem } from '../utils'
import { useFlowium } from '../client/FlowiumContext'
import type { FileSystemItem } from '@flowium/client'

interface DetailsbarProps {
    isCollapsed: boolean
    onCollapse: () => void
    selectedItem?: FileSystemItem | null
    children?: React.ReactNode
}

const Detailsbar: React.FC<DetailsbarProps> = ({ isCollapsed, onCollapse, selectedItem, children }) => {
    const { client, currentWorkspace, currentProject, user } = useFlowium()
    const [thumbnailUrl] = useThumbnailLoaderWithRetry(client, selectedItem, {
        workspaceId: currentWorkspace?.id ?? user?.workspace,
        projectId: currentProject?.id ?? user?.project
    })

    return (
        <div className={`shrink-0 border-l border-secondary bg-surface transition-[width] duration-300 ${
            isCollapsed
                ? 'relative hidden lg:block lg:w-14'
                : 'fixed inset-0 z-40 w-full lg:relative lg:inset-auto lg:z-auto lg:w-[min(26rem,32vw)]'
        }`}>
            <div className="flex items-center justify-between border-b border-secondary px-4 py-4">
                <button
                    type="button"
                    onClick={onCollapse}
                    aria-label="Toggle details panel"
                    className="flex items-center gap-2 text-sm font-medium text-content-secondary transition-colors hover:text-content"
                >
                    <SidebarCloseIcon className="" padding={0} />
                    {!isCollapsed && <span className="text-sm">Collapse</span>}
                </button>
            </div>
            {!isCollapsed && (
                <div className="flex h-[calc(100%-65px)] flex-col overflow-hidden">
                    {selectedItem && (
                        <div className="w-full px-4 pt-4">
                            <div className="flex justify-center rounded-2xl border border-secondary bg-surface-secondary p-4">
                                {isDirectoryItem(selectedItem) ? (
                                    <div className="flex h-32 w-32 items-center justify-center rounded-2xl border border-secondary bg-surface">
                                        <ListViewIcon size={80} className="text-content-tertiary" />
                                    </div>
                                ) : (
                                    <img
                                        src={thumbnailUrl}
                                        alt="preview"
                                        className="max-h-32 rounded-2xl border border-secondary object-contain"
                                    />
                                )}
                            </div>
                        </div>
                    )}
                    <div className="min-h-0 flex-1 overflow-y-auto">
                        {children}
                    </div>
                </div>
            )}
        </div>
    )
}

export default Detailsbar
